import Modal from '../components/ui/Modal';
import PageHeader from '../components/ui/PageHeader';
import { userFacingError } from '../utils/userFacingError';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
// @ts-ignore
import diff_match_patch, { DIFF_DELETE, DIFF_INSERT, DIFF_EQUAL } from 'diff-match-patch';
import { useAuth } from '../contexts/AuthContext';
import useEditorDraft from '../hooks/useEditorDraft';
import useUnsavedChanges from '../hooks/useUnsavedChanges';
import EditorSaveBar from '../components/EditorSaveBar';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import PublicationChecklist from '../components/PublicationChecklist';
import { checkPublication } from '../utils/publicationCheck';
import { PublicationService } from '../services/publicationService';
import { ArticleService } from '../services/articleService';
import { PlatformService } from '../services/platformService';
import { CategoryService } from '../services/categoryService';
import { DossierService } from '../services/dossierService';
import { ScheduleService } from '../services/scheduleService';
import { AuthService } from '../services/authService';
import { StorageService } from '../services/storageService';
import { MetadataService } from '../services/metadataService';
import { FeedbackService } from '../services/feedbackService';
import { KIService } from '../services/kiService';
import { UrlValidator } from '../utils/urlValidator';
import { formatContentAsHtml } from '../utils/htmlFormatter';
import { formatTags, parseTags } from '../utils/tagUtils';
import { createGermanDateTimeFromParts, formatPublishDateFromGermanDateTime } from '../utils/dateTimeUtils';
import { Article, Platform, Category, Dossier, ArticleStatus, User } from '../types';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { ArticleImageSelector } from '../components/ArticleImageSelector';
import { ScheduleConfirmModal } from '../components/ScheduleConfirmModal';
import PageLoader from '../components/ui/PageLoader';
import useSourceUrls, { SourceUrl } from '../hooks/useSourceUrls';
import { 
  ChevronLeftIcon, 
  ChevronRightIcon,
  CheckIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  FolderIcon,
  PhotoIcon,
  EyeIcon,
  SparklesIcon,
  PencilIcon,
  WrenchScrewdriverIcon,
  CloudArrowUpIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';

// 🚀 FEATURE FLAG: Inline-Diff Editor in Schritt 3
// Bei Problemen einfach auf 'false' setzen für altes Verhalten
const ENABLE_INLINE_DIFF_EDITOR = true;

interface WizardStep {
  id: number;
  title: string;
  description: string;
  icon: React.ReactNode;
}

const wizardSteps: WizardStep[] = [
  {
    id: 1,
    title: 'Plattform',
    description: 'Wo soll dein Artikel veröffentlicht werden?',
    icon: <GlobeAltIcon className="w-4 h-4" />
  },
  {
    id: 2,
    title: 'Lektorat',
    description: 'Text lektorieren und prüfen',
    icon: <DocumentTextIcon className="w-4 h-4" />
  },
  {
    id: 3,
    title: 'Nachbearbeitung',
    description: 'Lektorierten Text nachbearbeiten',
    icon: <PencilIcon className="w-4 h-4" />
  },
  {
    id: 4,
    title: 'Titel und Metadaten',
    description: 'Thema, Titel, Untertitel, Quellen',
    icon: <FolderIcon className="w-4 h-4" />
  },
  {
    id: 5,
    title: 'Artikel-Bild',
    description: 'Redaktion, Eigenes, KI-generieren',
    icon: <PhotoIcon className="w-4 h-4" />
  },
  {
    id: 6,
    title: 'Speichern oder Einreichen',
    description: 'Entwurf speichern oder einreichen',
    icon: <EyeIcon className="w-4 h-4" />
  }
];

interface ArticleFormData {
  title: string;
  content: string;
  platformId: string;
  categoryId: string;
  dossierId: string;
  topic: string;
  subtitle: string;
  teaser: string;
  tags: string;
  imagePrompt: string;
  imageSource: string;
  imageSourceUrl: string;
  imageStorageUrl: string;
  imageCaption: string;
  imageCaptionFromPrompt: string;
  imageType: 'eigenes' | 'ki' | 'redaktion';
  uploadedFile?: File;
  lektoratIntensity: 'rechtschreibung' | 'leicht' | 'tiefgreifend';
  feedbackText: string;
  lektoratCompleted: boolean;
  lektoratContent: string;
  showLektoratComparison: boolean;
  showDifferences: boolean;
  lektoratDifferences?: Array<[number, string]>;
  lektoratHighlightedText?: string;
  finalContent: string;
  manualEditCompleted: boolean;
  platformCategories: string; // Kommagetrennte Kategorien für Metadaten-API
  platformDossiers: string; // Kommagetrennte Dossiers für Metadaten-API
  lektoratHinweise: string;
  saveLektoratHinweise: boolean;
  sourceUrls: SourceUrl[];
}


export const ArticleWizardPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(() => {
    const stepParam = Number(searchParams.get('step'));
    if (Number.isNaN(stepParam)) return 1;
    return Math.min(Math.max(stepParam, 1), wizardSteps.length);
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const allowNavigationRef = React.useRef(false);
  
  // Modal-States
  const [showRedaktionsrichtlinien, setShowRedaktionsrichtlinien] = useState(false);
  const [showScheduleConfirmModal, setShowScheduleConfirmModal] = useState(false);
  
  // Admin-Funktion: Artikel für anderen Autor erstellen
  const [selectedAuthorId, setSelectedAuthorId] = useState(user?.id || '');
  const [availableAuthors, setAvailableAuthors] = useState<User[]>([]);
  
  // 🆕 Ref für ContentEditable Editor (Schritt 3)
  const contentEditableRef = React.useRef<HTMLDivElement>(null);
  const inlineHighlightTimeoutRef = React.useRef<number | null>(null);
  const inlineHighlightRestoreRef = React.useRef<{ scrollTop: number; cursorPos: number | null } | null>(null);
  const [inlineHighlightedHtml, setInlineHighlightedHtml] = useState('');
  const [isInlineEditing, setIsInlineEditing] = useState(false);
  
  // Formular-Daten
  const [formData, setFormData] = useState<ArticleFormData>({
    title: '',
    content: '',
    platformId: '',
    categoryId: '',
    dossierId: '',
    topic: '',
    subtitle: '',
    teaser: '',
         tags: '',
    imagePrompt: '',
         imageSource: '',
     imageSourceUrl: '',
     imageStorageUrl: '',
    imageCaption: '',
    imageCaptionFromPrompt: '',
    imageType: 'redaktion',
    lektoratIntensity: user?.preferredLektoratIntensity || 'leicht',
    feedbackText: '',
    lektoratCompleted: false,
    lektoratContent: '',
    showLektoratComparison: false,
    showDifferences: true,
    lektoratDifferences: undefined,
    lektoratHighlightedText: undefined,
    finalContent: '',
    manualEditCompleted: false,
    platformCategories: '', // Initialisiere neue Felder
    platformDossiers: '',
    lektoratHinweise: '',
    saveLektoratHinweise: false,
    sourceUrls: []
  });

  // Verfügbare Daten
  const [allPlatforms, setAllPlatforms] = useState<Platform[]>([]);
  const [userPlatforms, setUserPlatforms] = useState<Platform[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [allDossiers, setAllDossiers] = useState<Dossier[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<Category[]>([]);
  const [filteredDossiers, setFilteredDossiers] = useState<Dossier[]>([]);
  const [userSchedules, setUserSchedules] = useState<any[]>([]);
  
  // Metadaten-Cache für Bildunterschrift
  const [cachedMetadata, setCachedMetadata] = useState<any>(null);
  
  // Image-Loading State (für Bildgenerierung in ArticleImageSelector)
  const [isImageLoading, setIsImageLoading] = useState(false);

  const hasWizardProgress = useMemo(() => {
    const hasSourceUrls = formData.sourceUrls.some(source => source.url.trim() !== '');
    return (
      currentStep > 1 ||
      formData.platformId.trim() !== '' ||
      formData.categoryId.trim() !== '' ||
      formData.dossierId.trim() !== '' ||
      formData.topic.trim() !== '' ||
      formData.title.trim() !== '' ||
      formData.subtitle.trim() !== '' ||
      formData.teaser.trim() !== '' ||
      formData.tags.trim() !== '' ||
      formData.content.trim() !== '' ||
      formData.finalContent.trim() !== '' ||
      formData.feedbackText.trim() !== '' ||
      formData.lektoratContent.trim() !== '' ||
      formData.imagePrompt.trim() !== '' ||
      formData.imageSource.trim() !== '' ||
      formData.imageSourceUrl.trim() !== '' ||
      formData.imageStorageUrl.trim() !== '' ||
      formData.imageCaption.trim() !== '' ||
      formData.imageCaptionFromPrompt.trim() !== '' ||
      formData.manualEditCompleted ||
      formData.lektoratCompleted ||
      hasSourceUrls ||
      Boolean(formData.uploadedFile)
    );
  }, [currentStep, formData]);

  const draftValue = useMemo(() => {
    const { uploadedFile, lektoratDifferences, lektoratHighlightedText, ...fields } = formData;
    return { formData: { ...fields, sourceUrls: fields.sourceUrls.map(source => ({ ...source, isProcessing: false })) }, currentStep, selectedAuthorId };
  }, [formData, currentStep, selectedAuthorId]);
  const draft = useEditorDraft({ ownerId: user?.id, editorKey: 'new-text', value: draftValue,
    enabled: !!user, hasContent: hasWizardProgress,
    onRestore: saved => {
      if (!saved.formData || !Array.isArray(saved.formData.sourceUrls)) { toast.error('Bearbeitungsstand kann nicht wiederhergestellt werden.'); return; }
      setFormData(prev => ({ ...prev, ...saved.formData, uploadedFile: undefined,
        lektoratDifferences: undefined, lektoratHighlightedText: undefined }));
      setSelectedAuthorId(saved.selectedAuthorId || user?.id || '');
      const step = Math.min(Math.max(saved.currentStep || 1, 1), wizardSteps.length);
      setCurrentStep(step);
      const params = new URLSearchParams(searchParams); params.set('step', String(step)); setSearchParams(params, { replace: true });
    }
  });
  const blocker = useUnsavedChanges(() => !allowNavigationRef.current && (draft.dirty || isSaving || isImageLoading || (isLoading && hasWizardProgress) || (!!formData.uploadedFile && !formData.imageStorageUrl)), true, isSaving || isLoading || isImageLoading);
  const publicationChecks = checkPublication({ ...formData, content: formData.finalContent || formData.content },
    userPlatforms.find(p => p.id === formData.platformId), categories, allDossiers, formData.sourceUrls);
  const publicationReady = publicationChecks.every(check => check.ok);
  const goToStep = (step: number) => {
    const params = new URLSearchParams(searchParams); params.set('step', String(step)); setSearchParams(params);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const finishDraft = async () => {
    allowNavigationRef.current = true;
    try { await draft.complete(); } catch { toast.error('Artikel gespeichert. Der private Bearbeitungsstand konnte noch nicht entfernt werden.'); }
  };

  useEffect(() => {
    const stepParam = Number(searchParams.get('step'));
    const parsedStep = Number.isNaN(stepParam) ? 1 : Math.min(Math.max(stepParam, 1), wizardSteps.length);

    if (currentStep !== parsedStep) {
      setCurrentStep(parsedStep);
    }

    if (searchParams.get('step') !== String(parsedStep)) {
      const params = new URLSearchParams(searchParams);
      params.set('step', String(parsedStep));
      setSearchParams(params, { replace: true });
    }
  }, [currentStep, searchParams, setSearchParams]);

  // Lade verfügbare Daten beim Start
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const [allPlatformsData, categoriesData, allDossiersData] = await Promise.all([
          PlatformService.getAllPlatforms(),
          CategoryService.getAllCategories(),
          DossierService.getAllDossiers()
        ]);
        
        setAllPlatforms(allPlatformsData);
        setCategories(categoriesData);
        setAllDossiers(allDossiersData);
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Fehler beim Laden der Daten');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Quellen-URL-Funktionen
  const setSourceUrls = useCallback((updater: SourceUrl[] | ((prev: SourceUrl[]) => SourceUrl[])) => {
    setFormData(prev => {
      const nextSourceUrls = typeof updater === 'function'
        ? (updater as (prev: SourceUrl[]) => SourceUrl[])(prev.sourceUrls)
        : updater;
      return { ...prev, sourceUrls: nextSourceUrls };
    });
  }, []);

  const {
    addSourceUrl,
    updateSourceUrl,
    updateSourceTitle,
    removeSourceUrl,
    validateAndExtractSourceUrl
  } = useSourceUrls(formData.sourceUrls, setSourceUrls, {
    onMetadataError: (error) => {
      console.error('Error extracting metadata:', error);
    }
  });

  // Kein automatisches Quellen-Feld mehr - nur bei Bedarf über Button hinzufügen

  // Cleanup für generierte Bild-URLs beim Unmount

  // Lade Schedules für den Benutzer
  useEffect(() => {
    const loadUserSchedules = async () => {
      if (user) {
        try {
          const userSchedules = await ScheduleService.getSchedulesByAuthor(user.id);
          // Aktualisiere den Benutzer mit den geladenen Schedules
          if (userSchedules.length > 0) {
            // Wir können den user state nicht direkt ändern, aber wir können die Schedules separat speichern
            setUserSchedules(userSchedules);
          }
        } catch (error) {
          console.error('Error loading user schedules:', error);
        }
      }
    };

    loadUserSchedules();
  }, [user]);

  // Initialisiere selectedAuthorId wenn user geladen ist
  useEffect(() => {
    if (user && !selectedAuthorId) {
      setSelectedAuthorId(user.id);
    }
  }, [user, selectedAuthorId]);

  // Lade Autoren mit Schedules für gewählte Plattform (nur für Admin/Redakteur)
  useEffect(() => {
    const loadAuthorsForPlatform = async () => {
      if (!formData.platformId) {
        setAvailableAuthors([]);
        return;
      }

      const isAdmin = user?.roles?.includes('administrator');
      const isRedakteur = user?.roles?.includes('redakteur');
      
      if (!isAdmin && !isRedakteur) {
        return; // Normale Autoren sehen diese Funktion nicht
      }

      try {
        // Zeige Loading-State für bessere UX
        setAvailableAuthors([]);
        const authors = await ScheduleService.getAuthorsWithScheduleOnPlatform(formData.platformId);
        setAvailableAuthors(authors);
      } catch (error) {
        console.error('Fehler beim Laden der Autoren:', error);
        setAvailableAuthors([]);
      }
    };

    loadAuthorsForPlatform();
  }, [formData.platformId, user]);

  // Filtere Benutzer-Plattformen und Kategorien
  useEffect(() => {
    if (user && allPlatforms.length > 0) {
      // Filtere nur die Plattformen, die dem Benutzer zugeordnet sind
      const userPlatforms = allPlatforms.filter(platform => 
        user.assignedPlatforms.includes(platform.id)
      );
      setUserPlatforms(userPlatforms);
      
      // Wenn nur eine Plattform verfügbar ist, wähle sie automatisch aus
      if (userPlatforms.length === 1 && !formData.platformId) {
        updateFormData('platformId', userPlatforms[0].id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, allPlatforms]);

  // Filtere Kategorien und Dossiers basierend auf ausgewählter Plattform
  useEffect(() => {
    if (formData.platformId) {
      const platform = userPlatforms.find(p => p.id === formData.platformId);
      if (platform) {
        const filteredCategories = categories.filter(c => c.platformId === formData.platformId);
        const filteredDossiers = allDossiers.filter(d => d.platformId === formData.platformId);
        setFilteredCategories(filteredCategories);
        setFilteredDossiers(filteredDossiers);
      }
    } else {
      setFilteredCategories([]);
      setFilteredDossiers([]);
    }
  }, [formData.platformId, categories, allDossiers, userPlatforms]);

  // Lektorat-Präferenz speichern
  const saveLektoratPreference = useCallback(async (intensity: string) => {
    try {
      await AuthService.updateUserPreferences(user!.id, {
        preferredLektoratIntensity: intensity as 'rechtschreibung' | 'leicht' | 'tiefgreifend'
      });
    } catch (error) {
      console.error('Error saving lektorat preference:', error);
      // Fehler still behandeln, da es nicht kritisch ist
    }
  }, [user]);

  const updateFormData = useCallback((field: keyof ArticleFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Wenn sich die Lektorat-Intensität ändert, speichere die Präferenz
    if (field === 'lektoratIntensity' && user) {
      saveLektoratPreference(value);
    }
    
  }, [user, saveLektoratPreference]);

  // Funktion zur Berechnung der Unterschiede zwischen zwei Texten mit diff_match_patch
  const calculateDifferences = (originalText: string, lektoratText: string) => {
    const dmp = new diff_match_patch();
    const diff = dmp.diff_main(originalText, lektoratText);
    dmp.diff_cleanupSemantic(diff); // Verbessert die Lesbarkeit der Diffs
    return diff;
  };

  // Funktion zur Formatierung der Unterschiede in HTML
  const formatTextWithHighlights = (differences: Array<[number, string]>) => {
    let html = [];
    for (let i = 0; i < differences.length; i++) {
      const op = differences[i][0];    // Operation (DIFF_DELETE, DIFF_INSERT, DIFF_EQUAL)
      const text = differences[i][1]; // Text von der Operation
      const escapedText = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');

      switch (op) {
        case DIFF_INSERT:
          html.push(`<span class="bg-green-200 text-green-800 font-medium px-1 rounded">${escapedText}</span>`);
          break;
        case DIFF_DELETE:
          html.push(`<span class="bg-red-200 text-red-800 font-medium px-1 rounded line-through">${escapedText}</span>`);
          break;
        case DIFF_EQUAL:
          html.push(escapedText);
          break;
      }
    }
    return html.join('');
  };

  // 🆕 Funktion für Inline-Highlighting (nur geänderte Passagen markieren)
  // Für den ContentEditable Editor in Schritt 3
  const formatInlineHighlights = (differences: Array<[number, string]>) => {
    let html = [];
    for (let i = 0; i < differences.length; i++) {
      const op = differences[i][0];
      const text = differences[i][1];
      const escapedText = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');

      switch (op) {
        case DIFF_INSERT:
          // Nur Hinzugefügtes/Geändertes mit gelbem Hintergrund markieren
          html.push(`<mark class="bg-yellow-100 px-1 rounded" data-lektorat-change="true">${escapedText}</mark>`);
          break;
        case DIFF_DELETE:
          // Gelöschtes wird NICHT angezeigt (nur im final content die neue Version)
          break;
        case DIFF_EQUAL:
          html.push(escapedText);
          break;
      }
    }
    return html.join('');
  };

  // 🆕 Hilfsfunktionen für Cursor-Position (ContentEditable)
  const saveCursorPosition = (element: HTMLElement) => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;
    
    const range = selection.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(element);
    preCaretRange.setEnd(range.endContainer, range.endOffset);
    
    return preCaretRange.toString().length;
  };

  const restoreCursorPosition = (element: HTMLElement, position: number) => {
    const selection = window.getSelection();
    if (!selection) return;

    let charIndex = 0;
    let nodeStack: Node[] = [element];
    let node: Node | undefined;
    let foundStart = false;

    while (!foundStart && (node = nodeStack.pop())) {
      if (node.nodeType === Node.TEXT_NODE) {
        const textNode = node as Text;
        const nextCharIndex = charIndex + (textNode.length || 0);
        if (position <= nextCharIndex) {
          const range = document.createRange();
          range.setStart(textNode, position - charIndex);
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
          foundStart = true;
        }
        charIndex = nextCharIndex;
      } else {
        const childNodes = Array.from(node.childNodes);
        for (let i = childNodes.length - 1; i >= 0; i--) {
          nodeStack.push(childNodes[i]);
        }
      }
    }
  };

  const scheduleInlineHighlightUpdate = useCallback((plainText: string) => {
    if (inlineHighlightTimeoutRef.current) {
      window.clearTimeout(inlineHighlightTimeoutRef.current);
    }

    inlineHighlightTimeoutRef.current = window.setTimeout(() => {
      const differences = calculateDifferences(formData.content, plainText);
      const nextHtml = formatInlineHighlights(differences);

      if (contentEditableRef.current) {
        const cursorPos = saveCursorPosition(contentEditableRef.current);
        inlineHighlightRestoreRef.current = {
          scrollTop: contentEditableRef.current.scrollTop,
          cursorPos
        };
      }

      setInlineHighlightedHtml(nextHtml);
      setIsInlineEditing(false);
    }, 500);
  }, [formData.content]);

  useEffect(() => {
    if (currentStep !== 3) return;
    if (isInlineEditing) return;

    const differences = calculateDifferences(formData.content, formData.finalContent);
    const nextHtml = formatInlineHighlights(differences);
    setInlineHighlightedHtml(nextHtml);
  }, [currentStep, formData.content, formData.finalContent, isInlineEditing]);

  useEffect(() => {
    if (!contentEditableRef.current || !inlineHighlightRestoreRef.current) return;
    const { scrollTop, cursorPos } = inlineHighlightRestoreRef.current;

    requestAnimationFrame(() => {
      if (contentEditableRef.current) {
        contentEditableRef.current.scrollTop = scrollTop;
        if (cursorPos !== null) {
          restoreCursorPosition(contentEditableRef.current, cursorPos);
        }
      }
      inlineHighlightRestoreRef.current = null;
    });
  }, [inlineHighlightedHtml]);



  const nextStep = async () => {
    if (currentStep < wizardSteps.length) {
      // Scroll nach oben beim Schrittwechsel (besonders wichtig für Mobile)
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Beim Übergang von Schritt 1 zu Schritt 2: Kategorien und Dossiers laden
      if (currentStep === 1 && formData.platformId) {
        try {
          console.log('🔄 Lade Kategorien und Dossiers für Plattform:', formData.platformId);
          
          const [platformCategories, platformDossiers] = await Promise.all([
            CategoryService.getCategoriesByPlatform(formData.platformId),
            DossierService.getDossiersByPlatform(formData.platformId)
          ]);
          
          // Kategorien als kommagetrennten String erstellen
          const categoriesString = platformCategories
            .map(cat => cat.name)
            .join(', ');
          
          // Dossiers als kommagetrennten String erstellen
          const dossiersString = platformDossiers
            .map(dossier => dossier.title)
            .join(', ');
          
          console.log('📋 Kategorien String:', categoriesString);
          console.log('📋 Dossiers String:', dossiersString);
          
          // Formulardaten mit den neuen Strings aktualisieren
          setFormData(prev => ({
            ...prev,
            platformCategories: categoriesString,
            platformDossiers: dossiersString
          }));
          
        } catch (error) {
          console.error('❌ Fehler beim Laden der Kategorien/Dossiers:', error);
          // Bei Fehler leere Strings setzen
          setFormData(prev => ({
            ...prev,
            platformCategories: '',
            platformDossiers: ''
          }));
        }
      }
      
      const nextWizardStep = currentStep + 1;
      setCurrentStep(nextWizardStep);
      const params = new URLSearchParams(searchParams);
      params.set('step', String(nextWizardStep));
      setSearchParams(params, { replace: false });
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      // Scroll nach oben beim Schrittwechsel (besonders wichtig für Mobile)
      window.scrollTo({ top: 0, behavior: 'smooth' });
      const previousWizardStep = currentStep - 1;
      setCurrentStep(previousWizardStep);
      const params = new URLSearchParams(searchParams);
      params.set('step', String(previousWizardStep));
      setSearchParams(params, { replace: false });
    }
  };

  const validateCurrentStep = (): boolean => {
    switch (currentStep) {
      case 1:
        if (!formData.platformId) {
          toast.error('Bitte wähle eine Plattform aus');
          return false;
        }
        break;
      case 2:
        if (!formData.content.trim()) {
          toast.error('Bitte gib den Artikel-Inhalt ein');
          return false;
        }
        if (!formData.lektoratCompleted) {
          toast.error('Bitte starte das Lektorat, bevor du fortfährst');
          return false;
        }
        break;
      case 3:
        if (!formData.finalContent.trim()) {
          toast.error('Bitte bearbeite den lektorierten Text');
          return false;
        }
        break;
      case 4:
        if (!formData.categoryId) {
          toast.error('Bitte wähle eine Kategorie aus');
          return false;
        }
        if (!formData.topic.trim() || !formData.title.trim() || !formData.subtitle.trim() || !formData.teaser.trim()) {
          toast.error('Bitte fülle alle Pflichtfelder aus');
          return false;
        }
        // Prüfe Quellen-URLs: Wenn URLs vorhanden sind, müssen sie gültig sein
        if (formData.sourceUrls.length > 0) {
          const hasInvalidUrls = formData.sourceUrls.some(source => 
            source.url.trim() !== '' && !source.isValid
          );
          const hasProcessingUrls = formData.sourceUrls.some(source => 
            source.isProcessing
          );
          
          if (hasProcessingUrls) {
            toast.error('Bitte warte, bis die URL-Validierung abgeschlossen ist');
            return false;
          }
          
          if (hasInvalidUrls) {
            toast.error('Bitte korrigiere die ungültigen URLs oder entferne sie');
            return false;
          }
        }
        break;
      case 5:
        if (formData.imageType === 'ki' && !formData.imagePrompt.trim()) {
          toast.error('Bitte gib einen Bild-Prompt für die KI-Generierung ein');
          return false;
        }
        if (formData.imageType === 'eigenes') {
          if (!formData.imageSource.trim()) {
            toast.error('Bitte gib eine Bildquelle an');
            return false;
          }
          if (!formData.imageStorageUrl && !formData.uploadedFile) {
            toast.error('Bitte lade ein Bild hoch');
            return false;
          }
          if (!formData.imageCaption.trim()) {
            toast.error('Bitte gib eine Bildunterschrift an');
            return false;
          }
          // Bildquelle-URL ist zwingend erforderlich
          if (!formData.imageSourceUrl.trim()) {
            toast.error('Bitte gib eine Bildquelle-URL an');
            return false;
          }
          // URL-Validierung für Bildquelle-URL
          const validation = UrlValidator.validateImageSourceUrl(formData.imageSourceUrl);
          if (!validation.isValid) {
            toast.error(validation.error || 'Bitte gib eine gültige URL für die Bildquelle ein');
            return false;
          }
        }
        break;
    }
    return true;
  };

  const isNextDisabled = () => {
    if (isImageLoading) return true;
    if (currentStep === 1) {
      return !formData.platformId || (canSelectAuthor && availableAuthors.length === 0);
    }
    if (currentStep === 2) {
      return !formData.content.trim() || !formData.lektoratCompleted;
    }
    if (currentStep === 3) {
      return !formData.finalContent.trim();
    }
    if (currentStep === 5) {
      return false;
    }
    return false;
  };


  const handleNext = async () => {
    if (validateCurrentStep()) {
      await nextStep();
    }
  };


  const startLektorat = async (withHinweise: boolean = false) => {
    setIsLoading(true);
    try {
      // Plattform und Aktivierung prüfen; das technische Ziel liegt serverseitig in Infisical.
      const selectedPlatform = userPlatforms.find(p => p.id === formData.platformId);
      if (!selectedPlatform) {
        throw new Error('Keine Plattform ausgewählt');
      }
      
      if (!selectedPlatform.kiApiEnabled) {
        throw new Error('KI-API ist für diese Plattform nicht aktiviert');
      }
      
      const styleImprovement = formData.lektoratIntensity !== 'rechtschreibung';
      
      // Lektorat-Hinweise sammeln
      let allHinweise: string[] = [];
      
      // Gespeicherte Hinweise des Benutzers laden
      if (user) {
        try {
          const savedHinweise = await AuthService.getLektoratHinweise(user.id);
          allHinweise = [...savedHinweise];
        } catch (error) {
          console.warn('Fehler beim Laden der gespeicherten Hinweise:', error);
        }
      }
      
      // Aktuelle Hinweise hinzufügen, falls vorhanden
      if (withHinweise && formData.lektoratHinweise.trim()) {
        allHinweise.push(formData.lektoratHinweise.trim());
      }
      
      console.log('Sending lektorat request:', {
        platform: selectedPlatform.name,
        route: '/api/ki/lektorat',
        text: formData.content.substring(0, 100) + '...',
        styleImprovement: styleImprovement,
        intensity: formData.lektoratIntensity,
        hinweise: allHinweise
      });
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 Minuten Timeout
      
      // Lektorat ausschließlich über den Same-Origin-Proxy mit frischem Appwrite-JWT.
      const lektoratResponse = await KIService.submitLektoratPayload({
          text: formData.content, 
          styleImprovement: styleImprovement,
          intensity: formData.lektoratIntensity,
          lektoratHinweise: allHinweise.length > 0 ? allHinweise : undefined
        }, controller.signal);
      
      clearTimeout(timeoutId);
      
      // Lektorat Response verarbeiten
      if (!lektoratResponse.ok) {
        const errorText = await lektoratResponse.text();
        console.error('HTTP error response (Lektorat):', { status: lektoratResponse.status, statusText: lektoratResponse.statusText, body: errorText });
        throw new Error(`HTTP error! status: ${lektoratResponse.status} - ${lektoratResponse.statusText}`);
      }
      
      const lektoratResult = await lektoratResponse.json();
      console.log('Lektorat API response:', lektoratResult);
      
      let lektoratText = null;
      if (Array.isArray(lektoratResult) && lektoratResult.length > 0) {
        const firstItem = lektoratResult[0];
        if (firstItem.choices && firstItem.choices.length > 0) {
          const choice = firstItem.choices[0];
          if (choice.message && choice.message.content) {
            lektoratText = choice.message.content;
          }
        }
      }
      
      if (!lektoratText) {
        if (lektoratResult.lektoratText && lektoratResult.lektoratText.trim()) {
          lektoratText = lektoratResult.lektoratText;
        } else if (lektoratResult.text && lektoratResult.text.trim()) {
          lektoratText = lektoratResult.text;
        } else if (lektoratResult.content && lektoratResult.content.trim()) {
          lektoratText = lektoratResult.content;
        } else if (lektoratResult.result && lektoratResult.result.trim()) {
          lektoratText = lektoratResult.result;
        } else if (lektoratResult.output && lektoratResult.output.trim()) {
          lektoratText = lektoratResult.output;
        } else if (lektoratResult.data && typeof lektoratResult.data === 'string' && lektoratResult.data.trim()) {
          lektoratText = lektoratResult.data;
        } else if (lektoratResult.data && lektoratResult.data.text && lektoratResult.data.text.trim()) {
          lektoratText = lektoratResult.data.text;
        }
      }
      
      if (!lektoratText || lektoratText.trim() === '') {
        console.error('No valid text found in response:', lektoratResult);
        throw new Error('Kein lektorierter Text in der Antwort erhalten. API-Antwort: ' + JSON.stringify(lektoratResult));
      }
      
      const trimmedLektoratText = lektoratText.trim();
      const differences = calculateDifferences(formData.content, trimmedLektoratText);
      const highlightedText = formatTextWithHighlights(differences);
      
      // Speichere alle Daten in einem Aufruf
      setFormData(prev => ({
        ...prev,
        lektoratContent: trimmedLektoratText,
        lektoratCompleted: true,
        showLektoratComparison: true,
        showDifferences: true,
        lektoratDifferences: differences,
        lektoratHighlightedText: highlightedText,
        finalContent: trimmedLektoratText
      }));
      
      // Metadaten API-Aufruf (nach erfolgreichem Lektorat, mit lektoriertem Text)
      let metadataResult = null;
      try {
        console.log('🚀 Starte Metadaten-API-Aufruf mit lektoriertem Text...');
        console.log('📋 Verfügbare Kategorien:', formData.platformCategories);
        console.log('📋 Verfügbare Dossiers:', formData.platformDossiers);
        
        metadataResult = await MetadataService.generateMetadata(
          formData.content, // Verwende Originaltext für Metadaten-API
          formData.platformCategories,
          formData.platformDossiers,
          user?.name
        );
        console.log('✅ Metadaten erfolgreich generiert:', metadataResult);
        
        // Metadaten verarbeiten und Formularfelder ausfüllen
        if (metadataResult) {
          let metadataContent = null;
          
          console.log('🔍 Rohe Metadaten-Response:', metadataResult);
          console.log('🔍 Response-Typ:', typeof metadataResult);
          console.log('🔍 Response-Keys:', Object.keys(metadataResult));
          
          // Verschiedene Response-Formate unterstützen
          if (Array.isArray(metadataResult) && metadataResult.length > 0) {
            // Array-Format (wie in der Console gesehen)
            const firstItem = metadataResult[0];
            if (firstItem.choices && firstItem.choices.length > 0) {
              metadataContent = firstItem.choices[0].message.content;
              console.log('📋 Array-Format erkannt, content:', metadataContent);
            }
          } else if (metadataResult.choices && metadataResult.choices.length > 0) {
            // OpenAI-Format (direktes Objekt)
            metadataContent = metadataResult.choices[0].message.content;
            console.log('📋 OpenAI-Format erkannt, content:', metadataContent);
          } else if (metadataResult.content) {
            // Direktes content-Feld
            metadataContent = metadataResult.content;
            console.log('📋 Direktes content-Feld erkannt:', metadataContent);
          } else if (typeof metadataResult === 'string') {
            // String-Format
            metadataContent = metadataResult;
            console.log('📋 String-Format erkannt:', metadataContent);
          } else {
            // Fallback: Versuche das gesamte Objekt
            metadataContent = metadataResult;
            console.log('📋 Fallback: Verwende gesamtes Objekt:', metadataContent);
          }
          
          if (metadataContent) {
            console.log('📋 Verarbeite Metadaten:', metadataContent);
            console.log('📋 Typ der Metadaten:', typeof metadataContent);
            
            // Wenn content ein Objekt ist (strukturierte Metadaten)
            if (typeof metadataContent === 'object' && metadataContent !== null) {
              const metadata = metadataContent;
              console.log('📋 Strukturierte Metadaten gefunden:', metadata);
              console.log('📋 Metadaten-Keys:', Object.keys(metadata));
              
              // Formularfelder mit Metadaten ausfüllen
              const updatedFormData: any = {
                topic: metadata.thema || metadata.topic || '',
                title: metadata.titel || metadata.title || '',
                subtitle: metadata.untertitel || metadata.subtitle || '',
                teaser: metadata.teaser || '',
                tags: metadata.tags || '',
                imagePrompt: metadata.bild_prompt || metadata.imagePrompt || '',
                imageCaption: metadata.bild_unterschrift || metadata.imageCaption || '',
                imageCaptionFromPrompt: metadata.bild_unterschrift || metadata.imageCaption || ''
              };
              
              // Kategorie automatisch vorwählen, falls vorhanden
              if (metadata.kategorie) {
                const matchingCategory = filteredCategories.find(cat => 
                  cat.name.toLowerCase() === metadata.kategorie.toLowerCase()
                );
                if (matchingCategory) {
                  updatedFormData.categoryId = matchingCategory.id;
                  console.log('📋 Kategorie automatisch vorgewählt:', metadata.kategorie, '->', matchingCategory.id);
                } else {
                  console.log('⚠️ Keine passende Kategorie gefunden für:', metadata.kategorie);
                }
              }
              
              // Dossier automatisch vorwählen, falls vorhanden
              if (metadata.dossier) {
                const matchingDossier = filteredDossiers.find(dossier => 
                  dossier.title.toLowerCase() === metadata.dossier.toLowerCase()
                );
                if (matchingDossier) {
                  updatedFormData.dossierId = matchingDossier.id;
                  console.log('📋 Dossier automatisch vorgewählt:', metadata.dossier, '->', matchingDossier.id);
                } else {
                  console.log('⚠️ Kein passendes Dossier gefunden für:', metadata.dossier);
                }
              }
              
              console.log('📋 Aktualisierte Formulardaten:', updatedFormData);
              
              setFormData(prev => {
                const newData = {
                  ...prev,
                  ...updatedFormData
                };
                console.log('📋 Neue Formulardaten:', newData);
                return newData;
              });
              
              console.log('✅ Metadaten-Felder automatisch ausgefüllt:', updatedFormData);
              
              // Metadaten für späteren Zugriff cachen (für Bildunterschrift)
              setCachedMetadata(metadata);
              
              // Automatisch erkannte Links als Quellen hinzufügen
              if (metadata.links && Array.isArray(metadata.links) && metadata.links.length > 0) {
                console.log('🔗 Automatisch erkannte Links:', metadata.links);
                
                const apiSources: SourceUrl[] = metadata.links.map((link: string, index: number) => ({
                  id: `api-${Date.now()}-${index}`, // Eindeutige ID
                  url: link, // Link ist direkt ein String
                  title: '', // Titel wird später durch validateAndExtractSourceUrl gefüllt
                  isValid: false, // Wird durch Validierung gesetzt
                  isProcessing: false
                }));
                
                console.log('📋 API-Quellen erstellt:', apiSources);
                
                // Links zu den bestehenden Quellen hinzufügen
                setFormData(prev => ({
                  ...prev,
                  sourceUrls: [...prev.sourceUrls, ...apiSources]
                }));
                
                // Automatisch Titel für alle neuen Links abrufen
                apiSources.forEach(source => {
                  validateAndExtractSourceUrl(source.id, source.url);
                });
                
                toast.success(`Metadaten automatisch generiert und ${metadata.links.length} Quellen hinzugefügt!`);
              } else {
                toast.success('Metadaten automatisch generiert und ausgefüllt!');
              }
            } else if (typeof metadataContent === 'string') {
              console.log('⚠️ Metadaten sind als String, versuche JSON-Parsing...');
              try {
                const parsedMetadata = JSON.parse(metadataContent);
                console.log('📋 Geparste Metadaten:', parsedMetadata);
                
                const updatedFormData: any = {
                  topic: parsedMetadata.thema || parsedMetadata.topic || '',
                  title: parsedMetadata.titel || parsedMetadata.title || '',
                  subtitle: parsedMetadata.untertitel || parsedMetadata.subtitle || '',
                  teaser: parsedMetadata.teaser || '',
                  tags: parsedMetadata.tags || '',
                  imagePrompt: parsedMetadata.bild_prompt || parsedMetadata.imagePrompt || '',
                  imageCaption: parsedMetadata.bild_unterschrift || parsedMetadata.imageCaption || '',
                  imageCaptionFromPrompt: parsedMetadata.bild_unterschrift || parsedMetadata.imageCaption || ''
                };
                
                // Kategorie automatisch vorwählen, falls vorhanden
                if (parsedMetadata.kategorie) {
                  const matchingCategory = filteredCategories.find(cat => 
                    cat.name.toLowerCase() === parsedMetadata.kategorie.toLowerCase()
                  );
                  if (matchingCategory) {
                    updatedFormData.categoryId = matchingCategory.id;
                    console.log('📋 Kategorie automatisch vorgewählt (String):', parsedMetadata.kategorie, '->', matchingCategory.id);
                  } else {
                    console.log('⚠️ Keine passende Kategorie gefunden für:', parsedMetadata.kategorie);
                  }
                }
                
                // Dossier automatisch vorwählen, falls vorhanden
                if (parsedMetadata.dossier) {
                  const matchingDossier = filteredDossiers.find(dossier => 
                    dossier.title.toLowerCase() === parsedMetadata.dossier.toLowerCase()
                  );
                  if (matchingDossier) {
                    updatedFormData.dossierId = matchingDossier.id;
                    console.log('📋 Dossier automatisch vorgewählt (String):', parsedMetadata.dossier, '->', matchingDossier.id);
                  } else {
                    console.log('⚠️ Kein passendes Dossier gefunden für:', parsedMetadata.dossier);
                  }
                }
                
                setFormData(prev => ({
                  ...prev,
                  ...updatedFormData
                }));
                
                console.log('✅ Metadaten-Felder aus String geparst und ausgefüllt:', updatedFormData);
                toast.success('Metadaten automatisch generiert und ausgefüllt!');
              } catch (parseError) {
                console.error('❌ Fehler beim Parsen der Metadaten:', parseError);
                console.log('⚠️ Metadaten sind nicht im erwarteten Format:', metadataContent);
              }
            } else {
              console.log('⚠️ Metadaten sind nicht im erwarteten Format:', metadataContent);
            }
          }
        }
      } catch (metadataError: any) {
        console.error('❌ Metadaten-API Fehler:', metadataError);
        
        // Versuche Proxy als Fallback
        try {
          console.log('🔄 Versuche Proxy-Fallback...');
          metadataResult = await MetadataService.generateMetadataViaProxy(
            formData.content, // Verwende Originaltext für Metadaten-API
            formData.platformCategories,
            formData.platformDossiers,
            user?.name
          );
          console.log('✅ Metadaten via Proxy erfolgreich:', metadataResult);
          
          // Auch hier Metadaten verarbeiten
          if (metadataResult && metadataResult.content && typeof metadataResult.content === 'object') {
            const metadata = metadataResult.content;
            setFormData(prev => ({
              ...prev,
              topic: metadata.thema || prev.topic,
              title: metadata.titel || prev.title,
              subtitle: metadata.untertitel || prev.subtitle,
              teaser: metadata.teaser || prev.teaser,
              tags: metadata.tags || prev.tags,
              imagePrompt: metadata.bild_prompt || prev.imagePrompt,
              imageCaption: metadata.bild_unterschrift || prev.imageCaption,
              imageCaptionFromPrompt: metadata.bild_unterschrift || prev.imageCaptionFromPrompt
            }));
            
            // Metadaten für späteren Zugriff cachen (für Bildunterschrift)
            setCachedMetadata(metadata);
            
            // Automatisch erkannte Links als Quellen hinzufügen (Proxy-Fallback)
            if (metadata.links && Array.isArray(metadata.links) && metadata.links.length > 0) {
              console.log('🔗 Automatisch erkannte Links (Proxy):', metadata.links);
              
              const apiSources: SourceUrl[] = metadata.links.map((link: string, index: number) => ({
                id: `api-proxy-${Date.now()}-${index}`,
                url: link,
                title: '',
                isValid: false,
                isProcessing: false
              }));
              
              setFormData(prev => ({
                ...prev,
                sourceUrls: [...prev.sourceUrls, ...apiSources]
              }));
              
              // Automatisch Titel für alle neuen Links abrufen
              apiSources.forEach(source => {
                validateAndExtractSourceUrl(source.id, source.url);
              });
              
              toast.success(`Metadaten via Proxy generiert und ${metadata.links.length} Quellen hinzugefügt!`);
            } else {
              toast.success('Metadaten via Proxy generiert und ausgefüllt!');
            }
          }
        } catch (proxyError) {
          console.error('❌ Auch Proxy-Fallback fehlgeschlagen:', proxyError);
          console.warn('⚠️ Metadaten-API nicht verfügbar, aber Lektorat wird fortgesetzt');
        }
      }
      
      // Hinweise speichern, falls gewünscht
      if (withHinweise && formData.saveLektoratHinweise && user && formData.lektoratHinweise.trim()) {
        try {
          const currentHinweise = await AuthService.getLektoratHinweise(user.id);
          const newHinweise = [...currentHinweise, formData.lektoratHinweise.trim()];
          await AuthService.saveLektoratHinweise(user.id, newHinweise);
          toast.success('Hinweis für zukünftige Lektorate gespeichert!');
        } catch (error) {
          console.error('Fehler beim Speichern des Hinweises:', error);
          toast.error('Hinweis konnte nicht gespeichert werden');
        }
      }
      
      toast.success('Lektorat erfolgreich durchgeführt!');
    } catch (error: any) {
      console.error('Lektorat error:', error);
      if (error?.name === 'AbortError') {
        toast.error('Lektorat-Timeout: Der Vorgang dauerte zu lange (über 5 Minuten). Bitte versuche es mit einem kürzeren Text oder später erneut.');
      } else if (error instanceof TypeError && error.message?.includes('fetch')) {
        toast.error('Netzwerkfehler: Bitte prüfe deine Internetverbindung und versuche es erneut.');
      } else if (error.message?.includes('KI-API ist für diese Plattform nicht aktiviert')) {
        toast.error('KI-API ist für diese Plattform nicht aktiviert. Bitte kontaktiere die Administration.');
      } else if (error.message?.includes('Keine Plattform ausgewählt')) {
        toast.error('Bitte wähle zuerst eine Plattform aus.');
      } else {
        toast.error('Fehler beim Lektorat: ' + (userFacingError(error, 'Unbekannter Fehler')));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Hilfsfunktion: Finde nächsten freien Veröffentlichungstermin
  const findNextAvailableSlot = async (
    platformSchedule: any,
    platformId: string,
    authorId: string,
    author: User
  ): Promise<Date> => {
    // Nutze die neue scheduleService Funktion mit deutscher Zeitzone-Unterstützung
    return await ScheduleService.findNextAvailableSlotForAuthor(
      authorId,
      platformId,
      platformSchedule,
      author
    );
  };

  // Hilfsfunktion: Extrahiere Bildunterschrift aus gecachten Metadaten
  const getBildUnterschriftFromCache = (): string | null => {
    if (!cachedMetadata) {
      console.log('⚠️ Keine gecachten Metadaten verfügbar');
      return null;
    }
    
    if (cachedMetadata.bild_unterschrift) {
      console.log('✅ Bildunterschrift aus Cache gefunden:', cachedMetadata.bild_unterschrift);
      return cachedMetadata.bild_unterschrift;
    }
    
    console.log('⚠️ Keine bild_unterschrift in gecachten Metadaten');
    return null;
  };

  const saveArticle = async () => {
    if (isSaving || !user || !draft.ready || draft.recovery || !formData.platformId) return;
    setIsSaving(true);
    try {
      // Berechne das Veröffentlichungsdatum auch für Entwürfe
      let scheduledAt: Date | undefined;
      if (formData.platformId) {
        const platformSchedule = userSchedules.find(
          schedule => schedule.platformId === formData.platformId && schedule.isActive
        );

        if (platformSchedule) {
          // Finde nächsten freien Slot (prüft auf Konflikte)
          // Für Entwürfe verwenden wir immer den eingeloggten User (kein Admin-Override)
          scheduledAt = await findNextAvailableSlot(platformSchedule, formData.platformId, user!.id, user!);
        }
      }

      // Quellen separat aus sourceUrls extrahieren
      const sourcesArray = formData.sourceUrls
        .filter(source => source.isValid && source.url.trim())
        .map(source => ({
          id: source.id,
          url: source.url,
          title: source.title
        }));

      const articleData: Partial<Article> = {
        title: formData.title,
        content: formData.finalContent || formData.content, // Verwende finalen Text, falls vorhanden
        originalContent: formData.content, // Originaltext vor dem Lektorat
        platformId: formData.platformId,
        categoryId: formData.categoryId,
        dossierId: formData.dossierId,
        topic: formData.topic,
        subtitle: formData.subtitle,
        teaser: formData.teaser,
        tags: formData.tags,
        imageType: formData.imageType,
        imagePrompt: formData.imagePrompt,
        imageSource: formData.imageType === 'redaktion' ? 'Redaktion' : formData.imageSource,
        imageSourceUrl: formData.imageType === 'redaktion' ? 'https://www.ef-magazin.de' : formData.imageSourceUrl,
        imageStorageUrl: formData.imageType === 'redaktion' ? 'https://fra.cloud.appwrite.io/v1/storage/buckets/article-images/files/68e4ee76002eac11ff3e/view?project=689c6acc001bcf54fcdc': formData.imageStorageUrl,
        imageCaption: formData.imageType === 'redaktion' 
          ? (formData.imageCaption || getBildUnterschriftFromCache() || 'Bild von der Redaktion ausgewählt')
          : formData.imageCaption,
        imageCaptionFromPrompt: formData.imageCaptionFromPrompt || formData.imageCaption,
        lektoratIntensity: formData.lektoratIntensity,
        lektoratContent: formData.lektoratContent,
        sources: sourcesArray, // Quellen separat speichern
        status: 'draft' as ArticleStatus,
        authorId: user!.id,
        scheduledAt: scheduledAt ? scheduledAt.toISOString() : undefined // Konvertiere Date zu ISO-String
        // createdAt und updatedAt werden von Appwrite automatisch gesetzt
      };


      const savedArticle = await ArticleService.createArticle(articleData, user!);
      toast.success('Artikel erfolgreich erstellt!');
      await finishDraft();
      navigate(`/articles/${savedArticle.id}`);
    } catch (error) {
      console.error('Save article error:', error);
      toast.error('Fehler beim Speichern des Artikels');
    } finally {
      setIsSaving(false);
    }
  };

  const openScheduleConfirm = () => {
    if (!formData.platformId) {
      toast.error('Bitte wähle zuerst eine Plattform aus.');
      return;
    }
    if (!user || isSaving) return;
    if (!publicationReady) { toast.error('Bitte vervollständige die offenen Punkte der Einreichungsprüfung.'); goToStep(6); return; }
    setShowScheduleConfirmModal(true);
  };

  const submitArticle = async (confirmedSlot?: Date) => {
    if (isSaving || !user || !publicationReady || !confirmedSlot || confirmedSlot.getTime() <= Date.now()) { toast.error('Bitte Angaben und einen zukünftigen Veröffentlichungstermin bestätigen.'); return; }
    setIsSaving(true);
    try {
      // Sammle alle verfügbaren Daten für die API-Übermittlung
      const selectedPlatform = userPlatforms.find(p => p.id === formData.platformId);
      let selectedCategory = filteredCategories.find(c => c.id === formData.categoryId);
      let selectedDossier = filteredDossiers.find(d => d.id === formData.dossierId);

      if (!selectedCategory || !selectedDossier) {
        throw new Error('Für die gewählte Plattform ist keine Kategorie oder kein Dossier vorhanden.');
      }
      
                     // Berechne das nächste Veröffentlichungsdatum basierend auf den Terminen des Autors
      const getNextPublishDateTime = async () => {
          // Bestimme den Autor: Entweder der ausgewählte Autor (Admin-Funktion) oder der eingeloggte User
          const selectedAuthor = availableAuthors.find(a => a.id === selectedAuthorId) || user!;
          
          // Lade die Schedules des ausgewählten Autors
          const authorSchedules = await ScheduleService.getSchedulesByAuthor(selectedAuthor.id);
          
          // Finde den Termin für die ausgewählte Plattform
          const platformSchedule = authorSchedules.find(
            schedule => schedule.platformId === formData.platformId && schedule.isActive
          );
        
        if (confirmedSlot) {
          const publishDate = formatPublishDateFromGermanDateTime(confirmedSlot);
          const publishTime = platformSchedule?.time || '10:00:00';
          return { scheduledAt: confirmedSlot, publishDate, publishTime };
        }
        
        if (!platformSchedule) {
            // Fallback: Nächster Montag um 10:00 Uhr (Standard-Termin)
            const now = new Date();
            const currentDayOfWeek = now.getDay(); // 0=Sonntag, 1=Montag, etc.
            const targetDayOfWeek = 1; // Montag in JavaScript getDay() Format
            
            // Berechne die Tage bis zum nächsten Montag
            let daysToAdd = targetDayOfWeek - currentDayOfWeek;
            if (daysToAdd <= 0) {
              daysToAdd += 7; // Nächste Woche
            }
           
            // Erstelle das Ziel-Datum
            const targetDate = new Date(now);
            targetDate.setDate(now.getDate() + daysToAdd);
            
            // WICHTIG: Zeit ist IMMER deutsche Zeit (10:00), niemals lokale Browser-Zeitzone!
            const germanDateTime = createGermanDateTimeFromParts({
              year: targetDate.getFullYear(),
              month: targetDate.getMonth() + 1,
              day: targetDate.getDate(),
              hours: 10,
              minutes: 0
            });
            
            // Formatiere das Datum als DD.MM.YYYY für API-Payload
            const publishDate = formatPublishDateFromGermanDateTime(germanDateTime);
            
            // Standard-Zeit: 10:00 Uhr
            const publishTime = '10:00:00';
           
            return { scheduledAt: germanDateTime, publishDate, publishTime };
        }
         
         // Finde nächsten freien Slot (prüft auf Konflikte)
         const germanDateTime = await findNextAvailableSlot(platformSchedule, formData.platformId, selectedAuthor.id, selectedAuthor);
         
         // Formatiere das Datum als DD.MM.YYYY für API-Payload
         // WICHTIG: Verwende UTC-Methoden, da germanDateTime bereits in deutscher Zeit erstellt wurde
         const publishDate = formatPublishDateFromGermanDateTime(germanDateTime);
         
         // Verwende die gespeicherte Zeit aus der DB (IMMER deutsche Zeit)
         const publishTime = platformSchedule.time;
         
         console.log('DEBUG - getNextPublishDateTime:', {
           germanDateTime: germanDateTime.toISOString(),
           publishDate,
           publishTime,
           localTime: germanDateTime.toLocaleString('de-DE')
         });
         
         return { scheduledAt: germanDateTime, publishDate, publishTime };
       };
       
      const { scheduledAt, publishDate, publishTime } = await getNextPublishDateTime();
       
               // HTML-Formatierung für den Content (zentrale Funktion importiert)

        // Quellen zu Content hinzufügen
        const addSourcesToContent = (content: string, sourceUrls: SourceUrl[]): string => {
          if (sourceUrls.length === 0) return content;
          
          const validSources = sourceUrls.filter(source => source.isValid && source.url.trim());
          if (validSources.length === 0) return content;
          
          let sourcesText = '\n\n**Quellen:**\n\n';
          validSources.forEach(source => {
            sourcesText += `<a href="${source.url}" target="_blank">${source.title}</a>\n\n`;
          });
          
          return content + sourcesText;
        };

        const contentWithSources = addSourcesToContent(
          formData.finalContent || formData.content, 
          formData.sourceUrls
        );

        const formattedContent = formatContentAsHtml(contentWithSources);

        // selectedAuthor wurde bereits in getNextPublishDateTime definiert
        // Verwende die gleiche Logik hier nochmal für Konsistenz
        const selectedAuthor = availableAuthors.find(a => a.id === selectedAuthorId) || user!;

        const apiPayload = {
          // Artikel-Grunddaten
          title: formData.title,
          content: formattedContent,
          subtitle: formData.subtitle,
          teaser: formData.teaser,
          topic: formData.topic,
          tags: parseTags(formData.tags),
          seoTitle: formData.title, // SEO-Titel gleich wie Titel
          
          // Veröffentlichungstermin (berechnet basierend auf den Terminen des Autors)
          publishDate: publishDate,
          publishTime: publishTime,
          
          // Plattform-Informationen (nur Name)
          platform: {
            name: selectedPlatform?.name || ''
          },
          
                     // Kategorisierung
           category: {
             name: selectedCategory?.name || ''
           },
           dossier: {
             title: selectedDossier?.title || ''
           },
          
                                 // Bild-Informationen
            image: {
              prompt: formData.imagePrompt,
              url: formData.imageType === 'redaktion' ? 'https://appwrite.e-redaktion.org/v1/storage/buckets/article-images/files/68e4ee76002eac11ff3e/view?project=689c6acc001bcf54fcdc' : formData.imageStorageUrl, // Redaktions-Bild
              source: formData.imageType === 'redaktion' ? 'Redaktion' : formData.imageSource, // Dummy-Source für Redaktion
              sourceUrl: formData.imageType === 'ki' ? 'https://www.openai.com' : (formData.imageType === 'redaktion' ? 'https://www.google.de' : formData.imageSourceUrl), // Dummy-URL für Redaktion
              caption: formData.imageType === 'redaktion' 
                ? (formData.imageCaption || getBildUnterschriftFromCache() || 'Bild von der Redaktion ausgewählt')
                : formData.imageCaption,
              description: formData.imageType === 'redaktion' 
                ? (formData.imageCaption || getBildUnterschriftFromCache() || 'Bild von der Redaktion ausgewählt')
                : formData.imageCaption,
              type: formData.imageType
            },
          
          // Autor-Informationen (nur Name)
          author: {
            name: selectedAuthor.name // Verwende den ausgewählten Autor
          }
        };
      
      // JSON in Konsole ausgeben
      console.log('=== ARTIKEL-API-PAYLOAD ===');
      console.log(JSON.stringify(apiPayload, null, 2));
      console.log('=== ENDE API-PAYLOAD ===');
      
      // Artikel in der lokalen Datenbank speichern (OHNE Quellen im Content)
       // Quellen separat aus sourceUrls extrahieren
       const sourcesArray = formData.sourceUrls
         .filter(source => source.isValid && source.url.trim())
         .map(source => ({
           id: source.id,
           url: source.url,
           title: source.title
         }));

      // selectedAuthor wurde bereits weiter oben definiert (für apiPayload)

      const articleData: Partial<Article> = {
        title: formData.title,
        content: formData.finalContent || formData.content, // Content OHNE Quellen
        originalContent: formData.content,
        platformId: formData.platformId,
        categoryId: selectedCategory.id,
        dossierId: selectedDossier.id,
        topic: formData.topic,
        subtitle: formData.subtitle,
        teaser: formData.teaser,
        tags: formatTags(formData.tags),
        imageType: formData.imageType,
        imagePrompt: formData.imagePrompt,
        imageSource: formData.imageSource,
        imageSourceUrl: formData.imageSourceUrl,
        imageStorageUrl: formData.imageStorageUrl,
        imageCaption: formData.imageCaption,
        imageCaptionFromPrompt: formData.imageCaptionFromPrompt || formData.imageCaption,
        lektoratIntensity: formData.lektoratIntensity,
        lektoratContent: formData.lektoratContent,
        sources: sourcesArray, // Quellen separat im sources-Feld speichern
        status: 'ready' as ArticleStatus,
        authorId: selectedAuthor.id, // Verwende den ausgewählten Autor
        scheduledAt: scheduledAt ? scheduledAt.toISOString() : undefined, // Konvertiere Date zu ISO-String
        ...PublicationService.prepare(apiPayload)
        // createdAt und updatedAt werden von Appwrite automatisch gesetzt
      };

      const savedArticle = await ArticleService.createArticle(articleData, user!);
      await finishDraft();
      
      // Feedback speichern, falls vorhanden
      if (formData.feedbackText.trim()) {
        try {
          await FeedbackService.createFeedback({
            authorName: selectedAuthor.name, // Verwende den ausgewählten Autor
            feedback: formData.feedbackText.trim(),
            articleId: savedArticle.id
          });
          toast.success('Feedback erfolgreich gespeichert!');
        } catch (feedbackError) {
          console.error('Error saving feedback:', feedbackError);
          // Feedback-Fehler soll den Artikel-Submit nicht blockieren
          toast.error('Artikel gespeichert, aber Feedback konnte nicht gespeichert werden');
        }
      }
      
      toast.success('Artikel gespeichert und zur Veröffentlichung beauftragt. Du kannst den Browser jetzt schließen.', { duration: 6000 });
      navigate(`/articles/${savedArticle.id}`);
    } catch (error) {
      console.error('Submit article error:', error);
      toast.error(userFacingError(error, 'Fehler beim Einreichen des Artikels'), { duration: 10000 });
    } finally {
      setIsSaving(false);
    }
  };

  // Berechne Admin-Status für Validierung
  const isAdmin = user?.roles?.includes('administrator');
  const isRedakteur = user?.roles?.includes('redakteur');
  const canSelectAuthor = isAdmin || isRedakteur;

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Plattform auswählen *
              </label>
              {userPlatforms.length === 0 ? (
                <div className="p-4 border border-red-200 bg-red-50 rounded-lg">
                  <p className="text-red-800 text-sm">
                    Dir wurden noch keine Plattformen zugewiesen. Bitte kontaktiere die Administration,
                    um Plattformen zugewiesen zu bekommen, bevor du Artikel erstellen kannst.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {userPlatforms.map((platform) => (
                    <div
                      key={platform.id}
                      className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                        formData.platformId === platform.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => updateFormData('platformId', platform.id)}
                    >
                      <h3 className="font-medium text-gray-900">{platform.name}</h3>
                      <p className="text-sm text-gray-500">{platform.description}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Admin-Funktion: Artikel für anderen Autor erstellen */}
            {canSelectAuthor && formData.platformId && (
              <div className="border-2 border-blue-300 bg-blue-50 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <UserGroupIcon className="w-6 h-6 text-blue-600 mr-3" />
                  <h3 className="text-lg font-semibold text-blue-800">ADMIN-FUNKTION</h3>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-blue-900 mb-2">
                    Artikel erstellen für:
                  </label>
                  
                  {availableAuthors.length === 0 ? (
                    <div className="w-full px-4 py-2 border-2 border-blue-300 rounded-lg bg-white text-gray-500 flex items-center">
                      <LoadingSpinner size="sm" />
                      <span className="ml-2">Lade verfügbare Autoren...</span>
                    </div>
                  ) : (
                    <select
                      value={selectedAuthorId}
                      onChange={(e) => setSelectedAuthorId(e.target.value)}
                      className="w-full px-4 py-2 border-2 border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
                    >
                      {availableAuthors.map((author) => (
                        <option key={author.id} value={author.id}>
                          {author.name}
                        </option>
                      ))}
                    </select>
                  )}
                  
                  <p className="mt-2 text-sm text-blue-700 flex items-start">
                    <svg className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    Der Artikel wird diesem Autor zugeordnet und unter seinem Namen veröffentlicht.
                  </p>
                </div>
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 p-4 border border-blue-200 rounded-lg">
              <div className="flex items-start">
                <DocumentTextIcon className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
                <div>
                  <h3 className="text-blue-800 text-sm font-medium mb-1">
                    Text lektorieren
                  </h3>
                  <p className="text-blue-700 text-sm">
                    Im ersten Schritt lektorieren wir deinen Text. Du hast die Wahl aus drei Intensitäten. Jede richtet sich jedoch auch nach unseren{' '}
                    <button
                      type="button"
                      onClick={() => setShowRedaktionsrichtlinien(true)}
                      className="text-blue-600 hover:text-blue-800 underline font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded"
                    >
                      Redaktionsrichtlinien
                    </button>.
                  </p>
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lektorat-Intensität
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    formData.lektoratIntensity === 'rechtschreibung'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => updateFormData('lektoratIntensity', 'rechtschreibung')}
                >
                  <div className="flex items-center mb-2">
                    <PencilIcon className="w-5 h-5 text-gray-600 mr-2" />
                    <h3 className="font-medium text-gray-900">Rechtschreibung</h3>
                  </div>
                  <p className="text-sm text-gray-500">
                    Nur Orthografie, Grammatik und Zeichensetzung
                  </p>
                </div>
                
                <div
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    formData.lektoratIntensity === 'leicht'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => updateFormData('lektoratIntensity', 'leicht')}
                >
                  <div className="flex items-center mb-2">
                    <SparklesIcon className="w-5 h-5 text-gray-600 mr-2" />
                    <h3 className="font-medium text-gray-900">Leicht</h3>
                  </div>
                  <p className="text-sm text-gray-500">
                    Wie Rechtschreibung + leichte Stilglättung
                  </p>
                </div>
                
                <div
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    formData.lektoratIntensity === 'tiefgreifend'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => updateFormData('lektoratIntensity', 'tiefgreifend')}
                >
                  <div className="flex items-center mb-2">
                    <WrenchScrewdriverIcon className="w-5 h-5 text-gray-600 mr-2" />
                    <h3 className="font-medium text-gray-900">Tiefgreifend</h3>
                  </div>
                  <p className="text-sm text-gray-500">
                    Wie Rechtschreibung + Stilglättung + Umformulierung
                  </p>
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Artikel-Inhalt *
              </label>
              <Textarea
                value={formData.content}
                onChange={(e) => updateFormData('content', e.target.value)}
                placeholder="Schreibe hier den Inhalt deines Artikels …"
                rows={12}
              />
            </div>
            
            <div className="flex justify-center">
              {(() => {
                const selectedPlatform = userPlatforms.find(p => p.id === formData.platformId);
                const isKiApiAvailable = selectedPlatform?.kiApiEnabled;
                
                if (!selectedPlatform) {
                  return (
                    <div className="text-center">
                      <p className="text-gray-500 mb-2">Bitte wähle zuerst eine Plattform aus</p>
                      <Button disabled className="px-8">
                        <SparklesIcon className="w-4 h-4 mr-2" />
                        Lektorat starten
                      </Button>
                    </div>
                  );
                }
                
                if (!isKiApiAvailable) {
                  return (
                    <div className="text-center">
                      <p className="text-gray-500 mb-2">KI-API ist für diese Plattform nicht verfügbar</p>
                      <Button disabled className="px-8">
                        <SparklesIcon className="w-4 h-4 mr-2" />
                        Lektorat starten
                      </Button>
                    </div>
                  );
                }
                
                return (
                  <Button
                    onClick={() => startLektorat()}
                    disabled={!formData.content.trim() || isLoading}
                    className="px-8"
                  >
                    {isLoading ? (
                      <>
                        <LoadingSpinner size="sm" />
                        <span className="ml-2">Lektorat läuft...</span>
                      </>
                    ) : (
                      <>
                        <SparklesIcon className="w-4 h-4 mr-2" />
                        Lektorat starten
                      </>
                    )}
                  </Button>
                );
              })()}
            </div>
            
            {formData.showLektoratComparison && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">Lektorat-Vergleich</h3>
                  <p className="text-sm text-gray-600">
                    Links: Original | Mitte: Lektorat {formData.showDifferences ? '| Rechts: Unterschiede' : ''} ({formData.lektoratIntensity})
                  </p>
                </div>

                <div className={`grid grid-cols-1 ${formData.showDifferences ? 'xl:grid-cols-3' : 'lg:grid-cols-2'} gap-0 min-h-[500px]`}>
                  {/* Original Text */}
                  <div className="p-4 border-r border-gray-200 overflow-y-auto">
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                      <DocumentTextIcon className="w-5 h-5 mr-2" />
                      Original
                    </h4>
                    <div className="prose max-w-none">
                      <p className="whitespace-pre-wrap text-gray-700 leading-relaxed text-base">{formData.content}</p>
                    </div>
                  </div>

                  {/* Lektorat Text (ohne Hervorhebungen) */}
                  <div className="p-4 border-r border-gray-200 bg-blue-50 overflow-y-auto">
                    <h4 className="font-medium text-blue-900 mb-3 flex items-center">
                      <SparklesIcon className="w-5 h-5 mr-2" />
                      Lektorat
                    </h4>
                    <div className="prose max-w-none">
                      <p className="whitespace-pre-wrap text-blue-800 leading-relaxed text-base">{formData.lektoratContent}</p>
                    </div>
                  </div>

                  {/* Unterschiede mit Hervorhebungen */}
                  {formData.showDifferences && (
                    <div className="p-4 bg-green-50 overflow-y-auto">
                      <h4 className="font-medium text-green-900 mb-3 flex items-center">
                        <EyeIcon className="w-5 h-5 mr-2" />
                        Unterschiede
                      </h4>
                      <div className="prose max-w-none">
                        {formData.lektoratHighlightedText ? (
                          <div
                            className="whitespace-pre-wrap text-green-800 leading-relaxed text-base"
                            dangerouslySetInnerHTML={{ __html: formData.lektoratHighlightedText }}
                          />
                        ) : (
                          <p className="whitespace-pre-wrap text-green-800 leading-relaxed text-base">{formData.lektoratContent}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-6">
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-gray-400 rounded-full mr-2"></div>
                        <span className="text-sm text-gray-600">Original</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                        <span className="text-sm text-gray-600">Lektorat</span>
                      </div>
                      {formData.showDifferences && (
                        <>
                          <div className="flex items-center">
                            <div className="w-3 h-3 bg-green-200 rounded mr-2"></div>
                            <span className="text-sm text-gray-600">Hinzugefügt</span>
                          </div>
                          <div className="flex items-center">
                            <div className="w-3 h-3 bg-red-200 rounded mr-2"></div>
                            <span className="text-sm text-gray-600">Entfernt</span>
                          </div>
                        </>
                      )}
                    </div>
                                         <div className="flex items-center space-x-3">
                     </div>
                  </div>
                </div>
              </div>
            )}
            
            {formData.lektoratCompleted && !formData.showLektoratComparison && (
              <div className="p-4 border border-green-200 bg-green-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <CheckIcon className="w-5 h-5 text-green-600 mr-2" />
                    <div>
                      <p className="text-green-800 text-sm font-medium">
                        Lektorat erfolgreich durchgeführt!
                      </p>
                      <p className="text-green-700 text-xs mt-1">
                        Im nächsten Schritt kannst du den lektorierten Text noch manuell nachbearbeiten.
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      updateFormData('showLektoratComparison', true);
                      updateFormData('showDifferences', true);
                    }}
                  >
                    Vergleich anzeigen
                  </Button>
                </div>
              </div>
            )}

            {/* Lektorat-Hinweise Sektion */}
            {formData.lektoratCompleted && (
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Lektorat mit Hinweisen</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Falls du kreative Wortneuschöpfungen oder bewusste Fehler verwendest, die nicht korrigiert werden sollen,
                  kannst du hier Hinweise für das Lektorat eingeben.
                </p>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hinweise an das Lektorat
                    </label>
                    <Textarea
                      value={formData.lektoratHinweise}
                      onChange={(e) => updateFormData('lektoratHinweise', e.target.value)}
                      placeholder="z.B. 'Bitte Wort XY nicht korrigieren' oder 'Bitte diesen Satz nicht korrigieren'"
                      rows={3}
                    />
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="saveLektoratHinweise"
                      checked={formData.saveLektoratHinweise}
                      onChange={(e) => updateFormData('saveLektoratHinweise', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="saveLektoratHinweise" className="ml-2 block text-sm text-gray-700">
                      Hinweis für zukünftige Lektorate speichern
                    </label>
                  </div>
                  
                  <div className="flex justify-center">
                    <Button
                      onClick={() => startLektorat(true)}
                      disabled={!formData.lektoratHinweise.trim() || isLoading}
                      className="px-8"
                    >
                      {isLoading ? (
                        <>
                          <LoadingSpinner size="sm" />
                          <span className="ml-2">Lektorat mit Hinweisen läuft...</span>
                        </>
                      ) : (
                        <>
                          <SparklesIcon className="w-4 h-4 mr-2" />
                          Lektorat mit Hinweisen erneut durchführen
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );



      case 3:
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 p-4 border border-blue-200 rounded-lg">
              <div className="flex items-start">
                <PencilIcon className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
                <div>
                  <h3 className="text-blue-800 text-sm font-medium mb-1">
                    Manuelle Nachbearbeitung
                  </h3>
                  <p className="text-blue-700 text-sm">
                    Prüfe den lektorierten Text und passe ihn bei Bedarf weiter an.
                    {ENABLE_INLINE_DIFF_EDITOR && (
                      <span className="block mt-1">
                        <strong>💡 Gelb markierte Passagen</strong> wurden vom Lektorat geändert oder hinzugefügt.
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Finaler Artikel-Text *
              </label>
              
              {ENABLE_INLINE_DIFF_EDITOR ? (
                // 🆕 NEUE VERSION: ContentEditable mit Inline-Highlighting
                <div
                  ref={contentEditableRef}
                  contentEditable={draft.status !== 'loading' && !draft.recovery && !isSaving}
                  role="textbox"
                  aria-label="Finaler Artikeltext"
                  aria-multiline="true"
                  tabIndex={0}
                  suppressContentEditableWarning={true}
                  onInput={(e) => {
                    // Speichere Cursor-Position VOR dem Update
                    const cursorPos = saveCursorPosition(e.currentTarget);
                    
                    // Update den Text
                    const plainText = e.currentTarget.textContent || '';
                    updateFormData('finalContent', plainText);
                    setIsInlineEditing(true);
                    scheduleInlineHighlightUpdate(plainText);
                    
                    // Stelle Cursor-Position NACH dem Update wieder her
                    setTimeout(() => {
                      if (contentEditableRef.current && cursorPos !== null) {
                        restoreCursorPosition(contentEditableRef.current, cursorPos);
                      }
                    }, 0);
                  }}
                  onBlur={(e) => {
                    // Stelle sicher, dass der Text gespeichert wird
                    const plainText = e.currentTarget.textContent || '';
                    updateFormData('finalContent', plainText);
                    scheduleInlineHighlightUpdate(plainText);
                  }}
                  dangerouslySetInnerHTML={{ __html: inlineHighlightedHtml }}
                  className="w-full min-h-[400px] px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 leading-relaxed whitespace-pre-wrap overflow-y-auto"
                  style={{ outline: 'none' }}
                />
              ) : (
                // ⚙️ ALTE VERSION: Normales Textarea (Fallback)
                <Textarea
                  value={formData.finalContent}
                  onChange={(e) => updateFormData('finalContent', e.target.value)}
                  placeholder="Bearbeite hier den lektorierten Text …"
                  rows={16}
                />
              )}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            {/* Metadaten */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Artikel-Metadaten</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Thema *
                  </label>
                  <Input
                    value={formData.topic}
                    onChange={(e) => updateFormData('topic', e.target.value)}
                    placeholder="Hauptthema des Artikels"
                    maxLength={100}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Artikel-Titel *
                  </label>
                  <Input
                    value={formData.title}
                    onChange={(e) => updateFormData('title', e.target.value)}
                    placeholder="Gib den Titel des Artikels ein"
                    maxLength={200}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Untertitel *
                  </label>
                  <Input
                    value={formData.subtitle}
                    onChange={(e) => updateFormData('subtitle', e.target.value)}
                    placeholder="Untertitel für den Artikel"
                    maxLength={200}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Teaser für Social Media *
                  </label>
                  <Textarea
                    value={formData.teaser}
                    onChange={(e) => updateFormData('teaser', e.target.value)}
                    placeholder="Kurzer Teaser für Social Media Posts"
                    rows={3}
                    maxLength={280}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags (durch Komma getrennt)
                  </label>
                  <input
                    type="text"
                    value={formData.tags}
                    onChange={(e) => updateFormData('tags', e.target.value)}
                    placeholder="tag1, tag2, tag3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Kategorie & Dossier */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Organisation</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Kategorie auswählen *
                  </label>
                  {filteredCategories.length === 0 ? (
                    <div className="p-4 border border-red-200 bg-red-50 rounded-lg">
                      <p className="text-red-800 text-sm">
                        Keine Kategorien für die ausgewählte Plattform verfügbar. 
                        Bitte lege zuerst Kategorien in der Administration an.
                      </p>
                    </div>
                  ) : (
                    <select
                      value={formData.categoryId}
                      onChange={(e) => updateFormData('categoryId', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Kategorie auswählen</option>
                      {filteredCategories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-2">
                     Dossier auswählen *
                   </label>
                  {filteredDossiers.length === 0 ? (
                    <div className="p-4 border border-red-200 bg-red-50 rounded-lg">
                      <p className="text-red-800 text-sm">
                        Keine Dossiers für die ausgewählte Plattform verfügbar. 
                        Bitte lege zuerst Dossiers in der Administration an.
                      </p>
                    </div>
                  ) : (
                    <select
                      value={formData.dossierId}
                      onChange={(e) => updateFormData('dossierId', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Kein Dossier</option>
                      {filteredDossiers.map((dossier) => (
                        <option key={dossier.id} value={dossier.id}>
                          {dossier.title}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            </div>

            {/* Quellen */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Quellen</h3>
              
              <div className="space-y-4">
                {formData.sourceUrls.map((source, index) => (
                  <div key={source.id} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="relative">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Link
                          </label>
                          <Input
                            type="url"
                            value={source.url}
                            onChange={(e) => {
                              updateSourceUrl(source.id, e.target.value);
                              // Validiere auch bei onChange, aber mit kurzer Verzögerung
                              setTimeout(() => {
                                validateAndExtractSourceUrl(source.id, e.target.value);
                              }, 300);
                            }}
                            onBlur={() => validateAndExtractSourceUrl(source.id, source.url)}
                            onPaste={() => {
                              // Längere Verzögerung für Paste, damit die URL richtig eingefügt wird
                              setTimeout(() => {
                                validateAndExtractSourceUrl(source.id, source.url);
                              }, 200);
                            }}
                            placeholder="https://example.com/artikel"
                            className="pr-10 bg-white"
                          />
                          {source.isProcessing && (
                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                              <LoadingSpinner size="sm" />
                            </div>
                          )}
                        </div>
                        
                        <div className="mt-3">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Titel
                          </label>
                          <Input
                            type="text"
                            value={source.title}
                            onChange={(e) => updateSourceTitle(source.id, e.target.value)}
                            placeholder="Titel der Quelle"
                            className="bg-white"
                          />
                        </div>
                        
                        {source.url && !source.isValid && !source.isProcessing && (
                          <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-sm">
                            <p className="text-red-800">
                              Bitte gib eine vollständige URL zu einem spezifischen Artikel ein, nicht nur die Hauptseite.
                            </p>
                          </div>
                        )}
                      </div>
                      
                      {formData.sourceUrls.length > 1 && (
                        <button
                          onClick={() => removeSourceUrl(source.id)}
                          className="mt-7 p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                          type="button"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                
                <div className="flex justify-start">
                  <Button
                    type="button"
                    onClick={addSourceUrl}
                    className="bg-blue-600 hover:bg-blue-700 text-white border border-blue-600"
                  >
                    + Quelle hinzufügen
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );

      case 5:
        const selectedPlatform = userPlatforms.find(p => p.id === formData.platformId);
        return (
          <ArticleImageSelector
            imageType={formData.imageType}
            onImageTypeChange={(type) => updateFormData('imageType', type)}
            imageStorageUrl={formData.imageStorageUrl}
            imageSource={formData.imageSource}
            imageSourceUrl={formData.imageSourceUrl}
            imageCaption={formData.imageCaption}
            imageCaptionFromPrompt={formData.imageCaptionFromPrompt}
            imagePrompt={formData.imagePrompt}
            uploadedFile={formData.uploadedFile}
            onImageStorageUrlChange={(url) => updateFormData('imageStorageUrl', url)}
            onImageSourceChange={(source) => updateFormData('imageSource', source)}
            onImageSourceUrlChange={(url) => updateFormData('imageSourceUrl', url)}
            onImageCaptionChange={(caption) => updateFormData('imageCaption', caption)}
            onImagePromptChange={(prompt) => updateFormData('imagePrompt', prompt)}
            onUploadedFileChange={(file) => updateFormData('uploadedFile', file)}
            platform={selectedPlatform || null}
            cachedMetadata={cachedMetadata}
            onLoadingChange={setIsImageLoading}
          />
        );

                         case 6:
         return (
           <div className="space-y-6">
             <PublicationChecklist checks={publicationChecks} onFix={goToStep} />
             <div className="bg-gray-50 p-6 rounded-lg">
               <h3 className="text-lg font-medium text-gray-900 mb-4">Artikel-Übersicht</h3>
                
               <div className="space-y-4">
                 <div>
                   <span className="text-sm font-medium text-gray-500">Plattform:</span>
                   <p className="text-gray-900">
                     {userPlatforms.find(p => p.id === formData.platformId)?.name}
                   </p>
                 </div>
                 
                 <div>
                   <span className="text-sm font-medium text-gray-500">Thema:</span>
                   <p className="text-gray-900">{formData.topic}</p>
                 </div>
                 
                 <div>
                   <span className="text-sm font-medium text-gray-500">Titel:</span>
                   <p className="text-gray-900">{formData.title}</p>
                 </div>
                 
                 <div>
                   <span className="text-sm font-medium text-gray-500">Untertitel:</span>
                   <p className="text-gray-900">{formData.subtitle}</p>
                 </div>
                 
                 <div>
                   <span className="text-sm font-medium text-gray-500">Artikel-Text:</span>
                   <div className="mt-2 p-4 bg-white border rounded-lg max-h-64 overflow-y-auto">
                     <p className="text-gray-900 whitespace-pre-wrap text-sm leading-relaxed">
                       {formData.finalContent || formData.content}
                     </p>
                   </div>
                 </div>
                 
                 <div>
                   <span className="text-sm font-medium text-gray-500">Kategorie:</span>
                   <p className="text-gray-900">
                     {categories.find(c => c.id === formData.categoryId)?.name}
                   </p>
                 </div>
                 
                 <div>
                   <span className="text-sm font-medium text-gray-500">Dossier:</span>
                   <p className="text-gray-900">
                     {formData.dossierId 
                       ? allDossiers.find(d => d.id === formData.dossierId)?.title 
                       : 'Kein Dossier ausgewählt'
                     }
                   </p>
                 </div>
                 
                 <div>
                   <span className="text-sm font-medium text-gray-500">Tags:</span>
                                      <p className="text-gray-900">
                      {formData.tags || 'Keine Tags'}
                    </p>
                 </div>
                 
                                  {formData.imageStorageUrl && (
                    <div>
                      <span className="text-sm font-medium text-gray-500">Bild:</span>
                                           <img
                         src={StorageService.getImagePreviewUrlFromStorageUrl(formData.imageStorageUrl, 640, 360)}
                         alt="Preview"
                         className="w-full h-32 object-contain rounded mt-2"
                         onError={(e) => {
                           const img = e.currentTarget;
                           if (img.dataset.fallback !== 'true') {
                             img.dataset.fallback = 'true';
                             img.src = formData.imageStorageUrl;
                           }
                         }}
                       />
                    </div>
                  )}
                 
                                                    {formData.imageSource && (
                     <div>
                       <span className="text-sm font-medium text-gray-500">Bildquelle:</span>
                       <p className="text-gray-900">{formData.imageSource}</p>
                     </div>
                   )}

                   {formData.imageSourceUrl && (
                     <div>
                       <span className="text-sm font-medium text-gray-500">Bildquelle-URL:</span>
                       <p className="text-gray-900 break-all">{formData.imageSourceUrl}</p>
                     </div>
                   )}
                  
                  
                
                
                
                 {formData.imageCaption && (
                   <div>
                     <span className="text-sm font-medium text-gray-500">Bildunterschrift:</span>
                     <p className="text-gray-900">{formData.imageCaption}</p>
                   </div>
                 )}
               </div>
             </div>

             {/* Feedback-Feld */}
             <div className="bg-blue-50 p-6 rounded-lg">
               <h3 className="text-lg font-medium text-blue-900 mb-4">Dein Feedback</h3>
               <p className="text-blue-700 mb-4">
                 Deine Meinung ist uns wichtig und hilft uns, das System weiter zu verbessern.
               </p>
               <div>
                 <label htmlFor="feedbackText" className="block text-sm font-medium text-gray-700 mb-2">
                   Dein Feedback (optional)
                 </label>
                 <Textarea
                   id="feedbackText"
                   value={formData.feedbackText}
                   onChange={(e) => updateFormData('feedbackText', e.target.value)}
                   placeholder="Teile uns deine Erfahrungen mit dem System mit. Was hat dir gefallen? Was können wir verbessern?"
                   rows={4}
                   className="w-full"
                 />
               </div>
             </div>

             <div className="flex flex-wrap justify-end gap-3">
               <Button
                onClick={openScheduleConfirm}
                 disabled={isSaving || !publicationReady || isImageLoading}
                 className="flex-1"
               >
                 {isSaving ? (
                   <>
                     <LoadingSpinner size="sm" />
                     <span className="ml-2">Wird eingereicht...</span>
                   </>
                 ) : (
                   <>
                     <CloudArrowUpIcon className="w-4 h-4 mr-2" />
                     Artikel einreichen
                   </>
                 )}
               </Button>
             </div>
           </div>
         );

      

      default:
        return null;
    }
  };

  if (isLoading && currentStep === 1) {
    return <PageLoader />;
  }

                       return (
    <>
       <div className="w-full space-y-6">
      <PageHeader title={<>Artikel erstellen</>} description={<>Vom ersten Text bis zur geprüften Einreichung. Dein Bearbeitungsstand wird automatisch gesichert.</>} />
      <EditorSaveBar {...draft}>
        <Button variant="outline" onClick={saveArticle} loading={isSaving} disabled={!hasWizardProgress || !formData.platformId || !draft.ready || !!draft.recovery || isLoading || isImageLoading}>Als Entwurf speichern</Button>
      </EditorSaveBar>
      <fieldset disabled={draft.status === 'loading' || !!draft.recovery || isSaving} className="min-w-0 space-y-6">
              {/* Schritt-Navigation */}
        <div className="max-w-none mb-8">
         {/* Desktop: Vollständige Schrittkette */}
         <div className="hidden md:flex items-center justify-start overflow-x-auto pb-2">
                    <div className="flex items-center space-x-2">
            {wizardSteps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div
                  className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                    currentStep >= step.id
                      ? 'border-blue-500 bg-blue-500 text-white'
                      : 'border-gray-300 text-gray-400'
                  }`}
                >
                  {currentStep > step.id ? (
                    <CheckIcon className="w-4 h-4" />
                  ) : (
                    <div className="w-4 h-4">{step.icon}</div>
                  )}
                </div>
                
                <div className="ml-2">
                  <p className={`text-xs font-medium ${
                    currentStep >= step.id ? 'text-blue-600' : 'text-gray-500'
                  }`}>
                    {step.title}
                  </p>
                  <p className="text-xs text-gray-400 hidden sm:block">{step.description}</p>
                </div>
                
                {index < wizardSteps.length - 1 && (
                  <div className={`w-8 h-0.5 mx-2 ${
                    currentStep > step.id ? 'bg-blue-500' : 'bg-gray-300'
                  }`} />
                )}
              </div>
            ))}
         </div>
       </div>
       
       {/* Mobile: Minimalistische Anzeige mit Pfeilen */}
       <div className="flex md:hidden items-center justify-center">
         <button
           onClick={prevStep}
           disabled={currentStep === 1}
           className={`p-2 ${currentStep === 1 ? 'invisible' : 'visible'}`}
           aria-label="Vorheriger Schritt"
         >
           <ChevronLeftIcon className="w-6 h-6 text-blue-600" />
         </button>
         
         <div className="flex items-center px-4">
           <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-500 text-white mr-3">
             {wizardSteps[currentStep - 1].icon}
           </div>
           <div className="text-center">
             <p className="text-sm font-semibold text-gray-900">
               {wizardSteps[currentStep - 1].title}
             </p>
             <p className="text-xs text-gray-500">
               Schritt {currentStep}/{wizardSteps.length}
             </p>
           </div>
         </div>
         
         <button
           onClick={handleNext}
          disabled={currentStep === wizardSteps.length || isNextDisabled()}
          className={`p-2 ${currentStep === wizardSteps.length ? 'invisible' : 'visible'}`}
           aria-label="Nächster Schritt"
         >
           <ChevronRightIcon className="w-6 h-6 text-blue-600" />
         </button>
       </div>
     </div>

             {/* Schritt-Inhalt */}
       <div className={`bg-white rounded-lg shadow-sm border p-4 ${
         currentStep === 2 
           ? 'max-w-none' 
           : 'max-w-none'
       }`}>
        {renderStepContent()}
      </div>

             {/* Navigation */}
       <div className="max-w-none flex justify-between mt-8">
        <Button
          variant="outline"
          onClick={prevStep}
          disabled={currentStep === 1}
        >
          <ChevronLeftIcon className="w-4 h-4 mr-2" />
          Zurück
        </Button>

                 {currentStep < wizardSteps.length ? (
           <Button 
             onClick={handleNext}
             disabled={
              isNextDisabled()
             }
             variant={
              isNextDisabled()
                 ? "outline" 
                 : undefined
             }
           >
             Weiter
             <ChevronRightIcon className="w-4 h-4 ml-2" />
           </Button>
         ) : null}
      </div>
      </fieldset>
    </div>

      <ConfirmDialog open={blocker.state === 'blocked'} title="Änderungen noch nicht gesichert"
        description="Die letzte Änderung ist noch nicht gespeichert. Bleibe auf der Seite, bis die Sicherung bestätigt ist, oder verlasse die Seite ohne diese Änderung."
        cancelLabel="Auf Seite bleiben" confirmDisabled={isSaving || isLoading || isImageLoading} confirmLabel="Ohne letzte Änderung verlassen" danger onClose={() => { if (blocker.state === 'blocked') blocker.reset(); }}
        onConfirm={() => { if (blocker.state === 'blocked') blocker.proceed(); }} />
      {/* Redaktionsrichtlinien Modal */}
      {showRedaktionsrichtlinien && (
        <Modal title="Redaktionsrichtlinien" onClose={() => setShowRedaktionsrichtlinien(false)} wide>
            {/* Modal Body */}
            <div className="px-6 py-6 overflow-y-auto flex-1">
              <div className="prose prose-blue max-w-none">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Allgemeine Richtlinien</h3>
                <p className="text-gray-700 mb-4">
                ef-online ist das Nachrichten- und Meinungsjournal der Monatszeitschrift „eigentümlich frei“. ef-online dient dazu, aktuelle Themen aus einer ungewöhnlichen, in den Mainstream-Medien selten oder gar nicht zu findenden Sicht zu kommentieren. ef-online hat den Anspruch, sich gegen Entmündigung und Wohlstandsvernichtung durch die Politik zu wenden und aufzuzeigen, inwiefern Politiker, Staatsbürokraten und ihre willfährigen Medien den ehrlich arbeitenden Bürgern viel mehr schaden als nutzen.
                </p>
                <p className="text-gray-700 mb-4">
                ef-online richtet sich gegen die herrschende Politik und gegen die herrschenden Medien. Beides gilt es, immer wieder zu kritisieren und zu delegitimieren.
                </p>
                <p className="text-gray-700 mb-4">
                ef-online ist zuweilen implizit radikal liberal oder libertär, aber benutzt explizit keine Sektensprache wie „aus libertärer Sicht“ etc. Solche möglicherweise vom Autor gedachten Teilsätze sollten grundsätzlich ersatzlos gestrichen werden. (Aus „Aus Libertärer Sicht ist es gut, dass…“ wird dann „Es ist gut, dass…“) Für den Leser muss ef-online auch stilistisch ein modernes, gegenüber Medien und Politik kritisches, gut verständliches, informatives und unterhaltsames Gegenmedium sein. Inner-Sektendiskussionen (zum Beispiel mit oder gegen andere Libertäre) sind gänzlich zu vermeiden.
                </p>

                <h4 className="text-base font-semibold text-gray-900 mb-3 mt-6">Wichtige Punkte</h4>
                <p className="text-gray-700 mb-4">
                Folgende Rechtschreibregeln gelten wie im gedruckten ef:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-gray-700">
                  <li>Die ß-Regel nach „neuer Rechtschreibung“</li>
                  <li>Ansonsten „alte Rechtschreibung“</li>
                  <li>Alle Printmedien immer in Anführungszeichen. z.B. „Spiegel“ und „Süddeutsche Zeitung“</li>
                  <li>Ansonsten nur fremdsprachige Begriffe in Anführungszeichen (etwa: „political correctness“), sonst nichts</li>
                  <li>Tausenderschreibweise immer mit Punkt, wie z.B.: 70.000</li>
                  <li>Prozentangaben immer im Text ausgeschrieben, also etwa: 70 Prozent (nicht: „70 %“)</li>
                  <li>Keine Abkürzungen, also „etwa“ oder „beispielsweise“ statt „z.B.“</li>
                  <li>Keine Bandwurmsätze (Merkmal linker Soziologen), lieber öfter mal einen neuen Satz anfangen</li>
                  <li>Keine (unnötigen) Fremdwörter, unverständliche schon gar nicht!</li>
                  <li>Möglichst keine Klammern, lieber Gedankenstriche</li>
                  <li>Gedankenstriche (lang mit Freizeichen) und Bindestriche (kurz ohne Freizeichen) sind zwei Paar Schuhe</li>
                  <li>Wir wollen verständlich und populär schreiben, nicht im Wissenschaftsstil</li>
                  <li>ef und eigentümlich frei wird kursiv gesetzt, sonst nichts</li>
                  <li>Keine Fußnoten oder Links im Text, nur Literatur oder Internet mit entsprechenden Verweisen unter dem Text</li>
                  <li>Überschriftenprinzip aus drei Teilen: Thema (Schlagwortartig), Titel (möglichst peppig), Untertitel (Erklärend oder peppig)</li>
                </ul>

                <h4 className="text-base font-semibold text-gray-900 mb-3 mt-6">Lektorats-Intensitäten</h4>
                <div className="bg-gray-50 p-4 rounded-lg space-y-3 mt-3">
                  <div>
                    <strong className="text-blue-600">Rechtschreibung:</strong>
                    <p className="text-sm text-gray-600 mt-1">Korrektur von Rechtschreib- und Grammatikfehlern, grundlegende Zeichensetzung</p>
                  </div>
                  <div>
                    <strong className="text-blue-600">Leicht:</strong>
                    <p className="text-sm text-gray-600 mt-1">Zusätzlich Verbesserung von Satzbau und Formulierungen, Optimierung der Lesbarkeit</p>
                  </div>
                  <div>
                    <strong className="text-blue-600">Tiefgreifend:</strong>
                    <p className="text-sm text-gray-600 mt-1">Umfassende Überarbeitung inkl. Struktur, Stil und inhaltlicher Kohärenz</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end border-t">
              <Button
                onClick={() => setShowRedaktionsrichtlinien(false)}
                variant="primary"
              >
                Verstanden
              </Button>
            </div>
        </Modal>
      )}
      {showScheduleConfirmModal && user && (
        <ScheduleConfirmModal
          authorId={(availableAuthors.find(a => a.id === selectedAuthorId) || user).id}
          platformId={formData.platformId}
          currentUser={user}
          onClose={() => setShowScheduleConfirmModal(false)}
          onConfirm={(date) => {
            setShowScheduleConfirmModal(false);
            submitArticle(date);
          }}
        />
      )}
    </>
  );
};
